import React, { useEffect, useState } from "react";
import deliveryRouteService from "../api/deliveryRouteService";

const initialForm = {
    originCountryId: "",
    originCityId: "",
    originPostalCodeId: "",
    destinationCountryId: "",
    destinationCityId: "",
    destinationPostalCodeId: "",
};

const DeliveryRoutePage = () => {
    const role = localStorage.getItem("role")?.toLowerCase();
    const isAdmin = role === "admin" || role === "administrator";

    const [routes, setRoutes] = useState([]);
    const [form, setForm] = useState(initialForm);
    const [editingId, setEditingId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        fetchRoutes();
    }, []);

    const fetchRoutes = async () => {
        try {
            setLoading(true);
            const data = await deliveryRouteService.getAll();
            setRoutes(Array.isArray(data) ? data : []);
        } catch {
            setError("Error loading routes");
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const toPayload = () => ({
        ...(editingId ? { id: editingId } : {}),
        originCountryId: Number(form.originCountryId),
        originCityId: Number(form.originCityId),
        originPostalCodeId: Number(form.originPostalCodeId),
        destinationCountryId: Number(form.destinationCountryId),
        destinationCityId: Number(form.destinationCityId),
        destinationPostalCodeId: Number(form.destinationPostalCodeId),
    });

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            if (editingId) {
                await deliveryRouteService.update(editingId, toPayload());
            } else {
                await deliveryRouteService.create(toPayload());
            }

            setForm(initialForm);
            setEditingId(null);
            await fetchRoutes();
        } catch {
            setError("Error saving route");
        }
    };

    const handleEdit = (route) => {
        setEditingId(route.id);
        setForm({
            originCountryId: route.originCountryId ?? "",
            originCityId: route.originCityId ?? "",
            originPostalCodeId: route.originPostalCodeId ?? "",
            destinationCountryId: route.destinationCountryId ?? "",
            destinationCityId: route.destinationCityId ?? "",
            destinationPostalCodeId: route.destinationPostalCodeId ?? "",
        });
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this route?")) return;

        try {
            await deliveryRouteService.delete(id);
            await fetchRoutes();
        } catch {
            setError("Error deleting route");
        }
    };

    if (loading) return <p>Loading...</p>;

    return (
        <div className="w-full max-w-none">
            <h2 className="text-xl font-bold mb-4">Delivery Routes</h2>

            {error && <p className="text-red-600 mb-3">{error}</p>}

            <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-3 mb-6">
                {Object.keys(initialForm).map((key) => (
                    <input
                        key={key}
                        name={key}
                        type="number"
                        placeholder={key}
                        value={form[key]}
                        onChange={handleChange}
                        className="border p-2 rounded"
                        required
                    />
                ))}

                <div className="col-span-2 flex gap-2">
                    <button className="bg-blue-600 text-white px-4 py-2 rounded">
                        {editingId ? "Update" : "Create"}
                    </button>

                    {editingId && (
                        <button
                            type="button"
                            className="bg-gray-300 px-4 py-2 rounded"
                            onClick={() => {
                                setEditingId(null);
                                setForm(initialForm);
                            }}
                        >
                            Cancel
                        </button>
                    )}
                </div>
            </form>

            <table className="w-full border text-sm">
                <thead>
                    <tr className="bg-gray-100">
                        <th className="border p-2">ID</th>
                        <th className="border p-2">Origin</th>
                        <th className="border p-2">Destination</th>
                        <th className="border p-2">Actions</th>
                    </tr>
                </thead>

                <tbody>
                    {routes.map((route) => (
                        <tr key={route.id}>
                            <td className="border p-2">{route.id}</td>

                            <td className="border p-2">
                                {route.originCity?.name || route.originCityId} /{" "}
                                {route.originCountry?.name || route.originCountryId}
                            </td>

                            <td className="border p-2">
                                {route.destinationCity?.name || route.destinationCityId} /{" "}
                                {route.destinationCountry?.name || route.destinationCountryId}
                            </td>

                            <td className="border p-2 space-x-2">
                                <button
                                    className="text-blue-600"
                                    onClick={() => handleEdit(route)}
                                >
                                    Edit
                                </button>

                                {isAdmin && (
                                    <button
                                        className="text-red-600"
                                        onClick={() => handleDelete(route.id)}
                                    >
                                        Delete
                                    </button>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default DeliveryRoutePage;